// Keeps the app's data up to date. No React in here: the engine owns the
// state, the update checks, caching and retry, and the UI reads it through
// useAppState (useSyncExternalStore).
//
// One update check, in short: ask /api/versionInfo; reload the page if the app
// version changed; refetch whatever is not up to date (everything when the
// updateId changed, only players and points after a country change, anything
// that failed last time); each answer replaces its resource on its own, and a
// failed one keeps its last good value; once every resource is up to date the
// updateId is committed and a snapshot saved, otherwise the check is retried
// with backoff.
//
// The previous game day (the hidden "yesterday" view of Tänään) is loaded only
// on request and kept in memory, apart from the main check: see loadPreviousDay.

import { isAbort } from '../api/client';
import { LeagueUnavailableError, type Api } from '../api/endpoints';
import type { Snapshot } from '../cache/snapshot';
import type { Division, Game, Player, PointsByDate } from '../model/types';

export const POLL_INTERVAL_MS = 60_000;
export const BURST_WINDOW_MS = 5_000;
export const RETRY_DELAYS_MS = [15_000, 30_000, 60_000];
export const FAILING_NOTE_AFTER_MS = 5 * 60_000;
export const RELOAD_GUARD_KEY = 'nhlcountries_reloadedFor';
// A new app version found this soon after the page was shown or focused
// reloads it at once: the reader has only just arrived.
export const JUST_SHOWN_MS = 10_000;

// A check that fails just after the page was shown is retried sooner: on a
// phone the page often wakes up before the network does.
export const SHOWN_RETRY_DELAYS_MS = [2_000, 5_000];

export type ResourceStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface Resource<T> {
    value: T | undefined;
    // When the value was last replaced (epoch ms).
    updatedAt: number | undefined;
    error: string | undefined;
    status: ResourceStatus;
}

// The previous game day's games and the country's players who played in them.
export interface PreviousDay {
    date: string;
    country: string;
    scores: Game[];
    players: Player[];
}

export interface AppState {
    country: string;
    players: Resource<Player[]>;
    points: Resource<PointsByDate>;
    scores: Resource<Game[]>;
    standings: Resource<Division[]>;
    // Loaded on demand (the "all countries" stats view).
    allPlayers: Resource<Player[]>;
    // Loaded on demand (the previous game day on Tänään).
    previousDay: Resource<PreviousDay>;
    // The app version the backend reported when the page opened.
    appVersion: number | undefined;
    // The updateId of the data currently shown, once every resource has it.
    updateId: string | undefined;
    // The backend's own time of its last data update.
    lastUpdated: number | string | undefined;
    checking: boolean;
    // True once refreshing has been failing for more than five minutes.
    refreshFailing: boolean;
    // The backend does not serve the league's data (AHL switched off).
    unavailable: boolean;
}

type DataKey = 'players' | 'points' | 'scores' | 'standings' | 'allPlayers';
const MAIN_KEYS: DataKey[] = ['players', 'points', 'scores', 'standings'];
const COUNTRY_KEYS: DataKey[] = ['players', 'points'];

export interface EngineDeps {
    api: Api;
    getCountry(): string;
    loadSnapshot(country: string): Snapshot | undefined;
    saveSnapshot(snapshot: Snapshot): void;
    // Reloads the page.
    reload(): void;
    isVisible(): boolean;
    now(): number;
    sessionGet(key: string): string | null;
    sessionSet(key: string, value: string): void;
}

export interface SyncEngine {
    getState(): AppState;
    subscribe(listener: () => void): () => void;
    // Starts checking for updates and listening to page events.
    start(): void;
    stop(): void;
    setCountry(country: string): void;
    // Runs a check soon (merged with other triggers within five seconds).
    requestCheck(): void;
    // Runs a check now, e.g. from the retry button.
    retryNow(): void;
    // Asks for the all-players data; kept up to date from now on.
    loadAllPlayers(): void;
    // Asks for the previous game day's data (the game day before the current
    // one, as "YYYY-MM-DD"); kept in memory for that day.
    loadPreviousDay(date: string): void;
}

const emptyResource = <T>(): Resource<T> => ({ value: undefined, updatedAt: undefined, error: undefined, status: 'idle' });

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error));

interface Freshness {
    updateId: string | undefined;
    country: string | undefined;
}

// One answer about the previous game day, and whether it can be kept for good.
interface PreviousPart<T> {
    value: T;
    updateId: string | undefined;
    final: boolean;
}

export function createSyncEngine(deps: EngineDeps): SyncEngine {
    const { api } = deps;

    let country = deps.getCountry();
    let state: AppState = {
        country,
        players: emptyResource(),
        points: emptyResource(),
        scores: emptyResource(),
        standings: emptyResource(),
        allPlayers: emptyResource(),
        previousDay: emptyResource(),
        appVersion: undefined,
        updateId: undefined,
        lastUpdated: undefined,
        checking: false,
        refreshFailing: false,
        unavailable: false,
    };
    const listeners = new Set<() => void>();

    // What each resource's current value corresponds to.
    const fresh: Record<DataKey, Freshness> = {
        players: { updateId: undefined, country: undefined },
        points: { updateId: undefined, country: undefined },
        scores: { updateId: undefined, country: undefined },
        standings: { updateId: undefined, country: undefined },
        allPlayers: { updateId: undefined, country: undefined },
    };
    const controllers = new Map<DataKey, AbortController>();
    const inFlight = new Set<DataKey>();

    // The newest updateId the backend has announced.
    let targetUpdateId: string | undefined;
    // Bumped on every country change; answers from an older generation are ignored.
    let countryGeneration = 0;
    let allPlayersWanted = false;

    let running = false;
    let rerunRequested = false;
    let rerunImmediate = false;
    let lastStartAt = Number.NEGATIVE_INFINITY;
    let consecutiveFailures = 0;
    let failingSince: number | undefined;

    let started = false;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let burstTimer: ReturnType<typeof setTimeout> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    let reloadVersion: number | undefined;
    let shownAt = Number.NEGATIVE_INFINITY;
    let shownRetries = 0;
    // Visibility as last seen by the engine, to notice a missed visibilitychange.
    let seenVisible = true;
    let lastTickAt = Number.NEGATIVE_INFINITY;

    const setState = (patch: Partial<AppState>) => {
        state = { ...state, ...patch };
        listeners.forEach(listener => listener());
    };

    const setResource = <K extends DataKey>(key: K, patch: Partial<AppState[K]>) => {
        setState({ [key]: { ...state[key], ...patch } } as Partial<AppState>);
    };

    const isCountryKey = (key: DataKey) => COUNTRY_KEYS.includes(key);

    const isFresh = (key: DataKey) =>
        fresh[key].updateId === targetUpdateId
        && (!isCountryKey(key) || fresh[key].country === country)
        && state[key].value !== undefined;

    const staleKeys = (): DataKey[] => {
        const keys = MAIN_KEYS.filter(key => !isFresh(key));
        if (allPlayersWanted && !isFresh('allPlayers')) {
            keys.push('allPlayers');
        }
        return keys;
    };

    // ---- page reload on a new app version ---------------------------------

    const reloadIfPending = () => {
        if (reloadVersion === undefined || deps.sessionGet(RELOAD_GUARD_KEY) === String(reloadVersion)) {
            return;
        }
        // Reload only when nobody is reading: as the tab is hidden, or the moment
        // it is shown or focused again (also when the check made then finds the
        // new version), never in the middle of reading.
        deps.sessionSet(RELOAD_GUARD_KEY, String(reloadVersion));
        deps.reload();
    };

    // ---- snapshots ----------------------------------------------------------

    const restoreSnapshot = (target: string, { includeShared }: { includeShared: boolean }): Snapshot | undefined => {
        const snapshot = deps.loadSnapshot(target);
        if (snapshot === undefined) {
            return undefined;
        }
        const ready = <T>(value: T): Resource<T> => ({ value, updatedAt: snapshot.savedAt, error: undefined, status: 'ready' });

        const patch: Partial<AppState> = { players: ready(snapshot.players), points: ready(snapshot.points) };
        fresh.players = { updateId: snapshot.updateId, country: target };
        fresh.points = { updateId: snapshot.updateId, country: target };
        if (includeShared) {
            patch.scores = ready(snapshot.scores);
            patch.standings = ready(snapshot.standings);
            patch.updateId = snapshot.updateId;
            patch.lastUpdated = snapshot.lastUpdated;
            fresh.scores = { updateId: snapshot.updateId, country: undefined };
            fresh.standings = { updateId: snapshot.updateId, country: undefined };
        }
        setState(patch);
        return snapshot;
    };

    const saveCurrentSnapshot = (version: { updateId: string; lastUpdated?: number | string }) => {
        const { players, points, scores, standings } = state;
        if (players.value === undefined || points.value === undefined
            || scores.value === undefined || standings.value === undefined) {
            return;
        }
        deps.saveSnapshot({
            country,
            players: players.value,
            points: points.value,
            scores: scores.value,
            standings: standings.value,
            updateId: version.updateId,
            lastUpdated: version.lastUpdated,
            savedAt: deps.now(),
        });
    };

    // ---- fetching -----------------------------------------------------------

    const fetchResource = (key: DataKey, updateId: string | undefined): Promise<'ok' | 'failed' | 'stale'> => {
        const generation = countryGeneration;
        const requestedCountry = country;
        const controller = new AbortController();
        controllers.set(key, controller);
        inFlight.add(key);
        setResource(key, { status: 'loading' });

        const request = (): Promise<unknown> => {
            switch (key) {
                case 'players': return api.fetchPlayers(requestedCountry, controller.signal);
                case 'points': return api.fetchPoints(requestedCountry, controller.signal);
                case 'scores': return api.fetchScores(controller.signal);
                case 'standings': return api.fetchStandings(controller.signal);
                case 'allPlayers': return api.fetchAllPlayers(controller.signal);
            }
        };

        // An abort settles the fetch at once, whatever the api does with the signal.
        const aborted = new Promise<'stale'>(resolve =>
            controller.signal.addEventListener('abort', () => resolve('stale'), { once: true }));

        const answer = request().then(
            value => {
                if (isCountryKey(key) && generation !== countryGeneration) {
                    return 'stale' as const;
                }
                fresh[key] = { updateId, country: isCountryKey(key) ? requestedCountry : undefined };
                setResource(key, { value: value as never, updatedAt: deps.now(), error: undefined, status: 'ready' });
                return 'ok' as const;
            },
            error => {
                if (isAbort(error) || (isCountryKey(key) && generation !== countryGeneration)) {
                    return 'stale' as const;
                }
                console.warn(`Fetching ${key} failed`, error);
                setResource(key, { error: errorMessage(error), status: 'error' });
                return 'failed' as const;
            },
        );

        return Promise.race([answer, aborted]).finally(() => {
            if (controllers.get(key) === controller) {
                controllers.delete(key);
            }
            inFlight.delete(key);
        });
    };

    // ---- the previous game day ----------------------------------------------
    //
    // The backend saves the previous game day at the game day switch and never
    // changes it afterwards, so an answer that holds the requested day is kept
    // for as long as that day is asked for. An empty answer (no games, or the
    // backend lost its copy in a restart) or one still holding the day before
    // (asked just before the backend switched) is asked again when the
    // updateId changes; only the requested day's games and players are shown.

    let previousDate: string | undefined;
    let previousScores: PreviousPart<Game[]> | undefined;
    const previousPlayers = new Map<string, PreviousPart<Player[]>>();
    let previousController: AbortController | undefined;
    let previousRunning = false;
    let previousRerun = false;

    const needsFetch = (part: PreviousPart<unknown> | undefined) =>
        part === undefined || (!part.final && part.updateId !== targetUpdateId);

    const previousPart = <T>(
        items: T[], dateOf: (item: T) => string | undefined, date: string, updateId: string | undefined,
    ): PreviousPart<T[]> => ({
        value: items.filter(item => dateOf(item) === date),
        updateId,
        final: items.length > 0 && items.every(item => dateOf(item) === date),
    });

    const publishPreviousDay = () => {
        const players = previousPlayers.get(country);
        if (previousDate === undefined || previousScores === undefined || players === undefined) {
            return;
        }
        const shown = state.previousDay.value;
        if (state.previousDay.status === 'ready' && shown !== undefined && shown.date === previousDate
            && shown.country === country && shown.scores === previousScores.value && shown.players === players.value) {
            return;
        }
        setState({
            previousDay: {
                value: { date: previousDate, country, scores: previousScores.value, players: players.value },
                updatedAt: deps.now(),
                error: undefined,
                status: 'ready',
            },
        });
    };

    const refreshPreviousDay = async () => {
        if (previousDate === undefined || !started) {
            return;
        }
        if (previousRunning) {
            previousRerun = true;
            return;
        }
        previousRunning = true;
        const date = previousDate;
        const requestedCountry = country;
        const updateId = targetUpdateId;
        try {
            const wantScores = needsFetch(previousScores);
            const wantPlayers = needsFetch(previousPlayers.get(requestedCountry));
            if (!wantScores && !wantPlayers) {
                publishPreviousDay();
                return;
            }
            const controller = new AbortController();
            previousController = controller;
            setState({ previousDay: { ...state.previousDay, status: 'loading' } });
            try {
                const [scores, players] = await Promise.all([
                    wantScores ? api.fetchPreviousDayScores(controller.signal) : undefined,
                    wantPlayers ? api.fetchPreviousDayPlayers(requestedCountry, controller.signal) : undefined,
                ]);
                if (date !== previousDate || controller.signal.aborted) {
                    return;
                }
                if (scores !== undefined) {
                    previousScores = previousPart(scores, game => game.date, date, updateId);
                }
                if (players !== undefined) {
                    previousPlayers.set(requestedCountry, previousPart(players, player => player.latestGame?.date, date, updateId));
                }
                publishPreviousDay();
            } catch (error) {
                if (isAbort(error) || date !== previousDate || controller.signal.aborted) {
                    return;
                }
                // Asked again on the next check.
                console.warn('Fetching the previous game day failed', error);
                setState({ previousDay: { ...state.previousDay, error: errorMessage(error), status: 'error' } });
            }
        } finally {
            previousRunning = false;
            if (previousRerun) {
                previousRerun = false;
                void refreshPreviousDay();
            }
        }
    };

    // ---- the update check -------------------------------------------------

    const onCheckFailed = () => {
        consecutiveFailures++;
        failingSince ??= deps.now();
        const failingFor = deps.now() - failingSince;
        setState({ refreshFailing: failingFor >= FAILING_NOTE_AFTER_MS });

        const justShown = lastStartAt - shownAt <= JUST_SHOWN_MS && shownRetries < SHOWN_RETRY_DELAYS_MS.length;
        const delay = justShown
            ? SHOWN_RETRY_DELAYS_MS[shownRetries++]
            : RETRY_DELAYS_MS[Math.min(consecutiveFailures, RETRY_DELAYS_MS.length) - 1];
        clearTimeout(retryTimer);
        retryTimer = setTimeout(() => {
            retryTimer = undefined;
            requestCheck(true);
        }, delay);
    };

    const onCheckSucceeded = () => {
        consecutiveFailures = 0;
        failingSince = undefined;
        clearTimeout(retryTimer);
        retryTimer = undefined;
        if (state.refreshFailing) {
            setState({ refreshFailing: false });
        }
    };

    const runCheck = async () => {
        running = true;
        lastStartAt = deps.now();
        clearTimeout(retryTimer);
        retryTimer = undefined;
        setState({ checking: true });

        try {
            let version;
            try {
                version = await api.fetchVersionInfo();
            } catch (error) {
                console.warn('Version check failed', error);
                if (error instanceof LeagueUnavailableError) {
                    setState({ unavailable: true });
                }
                onCheckFailed();
                return;
            }
            if (state.unavailable) {
                setState({ unavailable: false });
            }

            if (state.appVersion === undefined) {
                setState({ appVersion: version.appVersion });
            } else if (version.appVersion !== state.appVersion) {
                reloadVersion = version.appVersion;
                if (!deps.isVisible() || deps.now() - shownAt <= JUST_SHOWN_MS) {
                    reloadIfPending();
                }
            }

            targetUpdateId = version.updateId;
            void refreshPreviousDay();
            const keys = staleKeys();
            const results = await Promise.all(keys.map(key => fetchResource(key, version.updateId)));
            // The all-players data is optional: a failure there does not make the update fail.
            const mainFailed = results.some((result, index) => result === 'failed' && keys[index] !== 'allPlayers');

            if (mainFailed) {
                onCheckFailed();
            } else if (MAIN_KEYS.every(isFresh)) {
                onCheckSucceeded();
                setState({ updateId: version.updateId, lastUpdated: version.lastUpdated });
                if (keys.some(key => key !== 'allPlayers')) {
                    saveCurrentSnapshot(version);
                }
            } else {
                // Not failed and not fresh: a country change got in the way, and
                // the check requested because of it runs next.
                setState({ lastUpdated: version.lastUpdated });
            }
        } finally {
            running = false;
            setState({ checking: false });
            if (rerunRequested) {
                const immediate = rerunImmediate;
                rerunRequested = false;
                rerunImmediate = false;
                requestCheck(immediate);
            }
        }
    };

    function requestCheck(immediate: boolean) {
        if (!started) {
            return;
        }
        if (running) {
            rerunRequested = true;
            rerunImmediate ||= immediate;
            return;
        }

        const sinceLastStart = deps.now() - lastStartAt;
        if (!immediate && sinceLastStart < BURST_WINDOW_MS) {
            if (burstTimer === undefined) {
                burstTimer = setTimeout(() => {
                    burstTimer = undefined;
                    requestCheck(false);
                }, BURST_WINDOW_MS - sinceLastStart);
            }
            return;
        }

        clearTimeout(burstTimer);
        burstTimer = undefined;
        void runCheck();
    }

    // ---- page events ------------------------------------------------------

    const markShown = () => {
        shownAt = deps.now();
        shownRetries = 0;
    };

    // The timer keeps running while the page is hidden but checks only while it
    // is visible. Browsers that freeze a hidden page (Firefox on Android) do not
    // always tell it that it is shown again; the overdue tick on waking does.
    const onTick = () => {
        const now = deps.now();
        const asleep = now - lastTickAt > 2 * POLL_INTERVAL_MS;
        lastTickAt = now;
        if (!deps.isVisible()) {
            seenVisible = false;
            return;
        }
        if (!seenVisible || asleep) {
            seenVisible = true;
            markShown();
            reloadIfPending();
        }
        requestCheck(false);
    };

    const startPolling = () => {
        clearInterval(pollTimer);
        lastTickAt = deps.now();
        pollTimer = setInterval(onTick, POLL_INTERVAL_MS);
    };

    const onVisibilityChange = () => {
        seenVisible = deps.isVisible();
        if (seenVisible) {
            markShown();
            startPolling();
            requestCheck(false);
        }
        reloadIfPending();
    };
    const onFocus = () => {
        markShown();
        reloadIfPending();
        requestCheck(false);
    };
    const onPageShow = (event: PageTransitionEvent) => {
        // Back from the back/forward cache.
        if (event.persisted) {
            markShown();
            requestCheck(true);
        }
    };
    const onOnline = () => requestCheck(false);

    // ---- public API ---------------------------------------------------------

    return {
        getState: () => state,

        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },

        start() {
            if (started) {
                return;
            }
            started = true;
            if (state.players.value === undefined) {
                restoreSnapshot(country, { includeShared: true });
            }
            document.addEventListener('visibilitychange', onVisibilityChange);
            window.addEventListener('focus', onFocus);
            window.addEventListener('online', onOnline);
            window.addEventListener('pageshow', onPageShow);
            seenVisible = deps.isVisible();
            if (seenVisible) {
                markShown();
            }
            startPolling();
            requestCheck(true);
        },

        stop() {
            started = false;
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('online', onOnline);
            window.removeEventListener('pageshow', onPageShow);
            clearInterval(pollTimer);
            clearTimeout(burstTimer);
            clearTimeout(retryTimer);
            pollTimer = burstTimer = retryTimer = undefined;
            controllers.forEach(controller => controller.abort());
            previousController?.abort();
        },

        setCountry(next) {
            if (next === country) {
                return;
            }
            country = next;
            countryGeneration++;
            COUNTRY_KEYS.forEach(key => controllers.get(key)?.abort());

            // Never show the previous country's players: use the new country's
            // snapshot if there is one, otherwise show nothing until the answer.
            setState({
                country: next,
                players: emptyResource(),
                points: emptyResource(),
            });
            fresh.players = { updateId: undefined, country: next };
            fresh.points = { updateId: undefined, country: next };
            if (restoreSnapshot(next, { includeShared: state.scores.value === undefined }) === undefined) {
                setState({
                    players: { ...emptyResource<Player[]>(), status: 'loading' },
                    points: { ...emptyResource<PointsByDate>(), status: 'loading' },
                });
            }
            if (previousDate !== undefined) {
                setState({ previousDay: emptyResource() });
                void refreshPreviousDay();
            }
            requestCheck(true);
        },

        requestCheck: () => requestCheck(false),

        retryNow: () => requestCheck(true),

        loadAllPlayers() {
            allPlayersWanted = true;
            if (targetUpdateId === undefined || isFresh('allPlayers') || inFlight.has('allPlayers')) {
                return;
            }
            void fetchResource('allPlayers', targetUpdateId);
        },

        loadPreviousDay(date) {
            if (date !== previousDate) {
                previousDate = date;
                previousScores = undefined;
                previousPlayers.clear();
                previousController?.abort();
                setState({ previousDay: emptyResource() });
            }
            void refreshPreviousDay();
        },
    };
}
